// Premiers pas par rôle (D-091) : les étapes, dans l'ordre de la base (_guide_steps, redéfinie par la migration
// 20261102600000_modules.sql, vérifié par guide.test.ts), cochées d'après les données
// (onboarding_state). Tutoiement ; vouvoiement pour le partenaire (D-074). Logique pure, testée.
// D-122 : « Ce que le club utilise » en 2e étape du bureau ; « Inviter le reste du bureau » et « Mettre en ligne la page
// du club » (avec son module) deviennent facultatives : elles ne retiennent plus le bloc ni les relances.
import { agree, type TeamGender } from '../domain/labels';

export type GuideRole = 'bureau' | 'technique' | 'coach' | 'joueur' | 'parent' | 'partenaire';

/** Les étapes de chaque rôle (responsable technique : d'après les données de son espace, D-093). */
export const STEP_KEYS: Record<GuideRole, string[]> = {
  bureau: ['club', 'modules', 'brand', 'import', 'coachs', 'bureau', 'page'],
  technique: ['diplomes', 'projet', 'theme', 'evaluation'],
  coach: ['equipe', 'joueuses', 'seance', 'convoquer'],
  joueur: ['compte', 'ecran', 'notifs', 'repondre'],
  parent: ['compte', 'ecran', 'notifs', 'repondre'],
  partenaire: ['fiche', 'visibilite', 'appel'],
};

/** Les étapes facultatives (D-122) : « quand tu veux », hors du compteur, du bloc de l'accueil et des relances. */
export const OPTIONAL_STEPS = new Set(['bureau:bureau', 'bureau:page']);

/** Durées annoncées (minutes), les mêmes que la base. */
export const STEP_MINUTES: Record<string, number> = {
  'bureau:modules': 1, 'bureau:brand': 1, 'bureau:import': 5, 'bureau:coachs': 2, 'bureau:bureau': 1, 'bureau:page': 3,
  'coach:joueuses': 2, 'coach:seance': 3, 'coach:convoquer': 1,
  'technique:diplomes': 5, 'technique:projet': 10, 'technique:theme': 3, 'technique:evaluation': 3,
  'joueur:ecran': 1, 'joueur:notifs': 1, 'joueur:repondre': 1, 'parent:ecran': 1, 'parent:notifs': 1, 'parent:repondre': 1,
  'partenaire:visibilite': 1, 'partenaire:appel': 1,
};

export interface FirstStep {
  key: string;
  title: string;
  sub: string;
  done: boolean;
  /** La visite « Montre-moi » de l'étape. */
  tour?: string;
  /** Libellé du bouton (« Montre-moi » par défaut). */
  cta?: string;
  /** Une page plutôt qu'une visite (« Ce que le club utilise » : « Choisir »). */
  href?: string;
  /** Facultative (D-122) : « quand tu veux », hors du compteur et du bloc de l'accueil. */
  optional?: boolean;
  /** Acquis d'office (« Ton compte est relié ») : affiché, mais hors du compteur, qui suit le titre
   *  (« 3 réglages, 1 minute » : 0 / 3 au départ, D-091 ; D-121, lot 0). */
  given?: boolean;
}

/** Ce que l'écran sait pour rendre les étapes concrètes (noms, dates, compteurs). */
export interface StepInfo {
  done: Record<string, boolean>;
  n?: Record<string, number | undefined>;
  slug?: string | null;
  hostBase?: string | null;
  gender?: TeamGender;
  teamName?: string;
  personName?: string;
  kidName?: string;
  /** Prochaine séance (« ce soir », « mardi ») et prochain match (« samedi », « SF1 – ASC Valdorne · 31 oct. »). */
  training?: { when: string; today: boolean; time: string; title?: string } | null;
  match?: { day: string; label: string } | null;
  partnerName?: string;
  clubName?: string;
  ask?: string | null;
  /** Bureau : équipes de la saison reliées à la FFBB ; coach : son équipe l'est-elle ? (inconnu : rien n'est affirmé). */
  ffbbLinked?: number;
  teamLinked?: boolean;
  /** Les étapes facultatives que la base propose (« page » seulement avec le module, D-122) ; sans : toutes. */
  optional?: string[];
}

/** « Le club est créé » : ce qui est vrai des équipes de la saison, reliées à la FFBB ou non (D-121, lot 0). */
export function clubTeamsLine(teams: number, linked?: number): string {
  if (!teams) return 'Ajoute tes équipes : Club › Équipes et coachs';
  const n = `${teams} ${teams > 1 ? 'équipes' : 'équipe'}`;
  if (linked === undefined) return n;
  if (linked >= teams) return `${teams > 1 ? 'Équipes engagées reliées' : 'Équipe engagée reliée'} à la FFBB · ${n}`;
  if (!linked) return `${n} · pas encore ${teams > 1 ? 'reliées' : 'reliée'} à la FFBB`;
  return `${n}, dont ${linked} ${linked > 1 ? 'reliées' : 'reliée'} à la FFBB`;
}

const min = (role: GuideRole, key: string) => STEP_MINUTES[`${role}:${key}`] ?? 0;
const withMin = (s: string, role: GuideRole, key: string) => (min(role, key) ? `${s} · ${min(role, key)} min` : s);

/** Les étapes d'un rôle, avec leurs textes. */
export function firstSteps(role: GuideRole, info: StepInfo): FirstStep[] {
  const d = (k: string) => info.done[k] ?? false;
  const n = (k: string) => info.n?.[k];
  const g = info.gender ?? 'F';
  switch (role) {
    case 'bureau': {
      const teams = n('club') ?? 0;
      const offered = (k: string) => !info.optional || info.optional.includes(k);
      const steps: FirstStep[] = [
        { key: 'club', title: 'Le club est créé', done: d('club'),
          sub: clubTeamsLine(teams, info.ffbbLinked), tour: teams ? undefined : 'saison-equipes' },
        { key: 'modules', title: 'Ce que le club utilise', sub: withMin('Ce qui prend du temps au bureau : Arceau propose ses modules', role, 'modules'), done: d('modules'),
          href: '#/club/modules/choisir', cta: 'Choisir' },
        { key: 'brand', title: 'Ton logo et tes couleurs', sub: 'Ils habillent l’app et ses liens partagés', done: d('brand'), tour: 'pp-brand' },
        { key: 'import', title: 'Importer les licenciés', sub: withMin('L’export Excel de FBI', role, 'import'), done: d('import'), tour: 'importer-licencies' },
        { key: 'coachs', title: 'Inviter les coachs', sub: withMin('Un lien par coach', role, 'coachs'), done: d('coachs'), tour: 'pp-coachs' },
        { key: 'bureau', title: 'Inviter le reste du bureau', sub: withMin('Trésorerie, secrétariat', role, 'bureau'), done: d('bureau'), tour: 'pp-bureau', optional: true },
        { key: 'page', title: 'Mettre en ligne la page du club', done: d('page'), tour: 'page-publique', optional: true,
          sub: withMin(info.slug && info.hostBase ? `${info.slug}.${info.hostBase}` : 'Matchs, résultats, contact', role, 'page') },
      ];
      return steps.filter(s => !s.optional || offered(s.key));
    }
    case 'technique':
      return [
        { key: 'diplomes', title: 'Noter les diplômes des coachs et vérifier les obligations par niveau', done: d('diplomes'), tour: 'pp-rt-diplomes',
          sub: withMin('BF, CQP, DE JEPS ; le modèle de la ligue à confirmer', role, 'diplomes') },
        { key: 'projet', title: 'Écrire le projet sportif', sub: withMin('Le fil rouge et les objectifs du cycle en cours', role, 'projet'), done: d('projet'), tour: 'pp-rt-projet' },
        { key: 'theme', title: 'Publier le thème du mois', sub: withMin('Une proposition aux coachs, avec ses exercices', role, 'theme'), done: d('theme'), tour: 'pp-rt-theme' },
        { key: 'evaluation', title: 'Lancer la première campagne d’évaluation', sub: withMin('Les coachs notent, tu vois tout', role, 'evaluation'), done: d('evaluation'), tour: 'pp-rt-eval' },
      ];
    case 'coach':
      return [
        { key: 'equipe', title: 'Ton équipe est prête', done: d('equipe'),
          sub: info.teamLinked === undefined ? 'Créée par le club' : info.teamLinked ? 'Créée et reliée à la FFBB par le club' : 'Créée par le club · pas encore reliée à la FFBB' },
        { key: 'joueuses', title: `Inviter ${agree(g, 'tes joueurs', 'tes joueuses')}`, sub: 'Un lien pour le groupe WhatsApp de l’équipe', done: d('joueuses'), tour: 'inviter-equipe' },
        { key: 'seance', title: info.training?.today ? 'Préparer la séance de ce soir' : 'Préparer ta prochaine séance',
          sub: 'Exercices du club, durée, consignes', done: d('seance'), tour: 'plan-seance' },
        { key: 'convoquer', title: info.match ? `Convoquer pour ${info.match.day}` : 'Convoquer pour le prochain match',
          sub: info.match?.label ?? 'Composition, renforts : l’équipe est prévenue', done: d('convoquer'), tour: 'convoquer-match' },
      ];
    case 'joueur':
    case 'parent':
      return [
        { key: 'compte', title: 'Ton compte est relié', done: d('compte'), given: true,
          sub: role === 'parent' ? `Parent de ${info.kidName ?? 'ton enfant'}` : [info.personName, info.teamName].filter(Boolean).join(' · ') || 'Ta fiche du club' },
        { key: 'ecran', title: 'Mettre l’app sur l’écran d’accueil', sub: 'Elle s’ouvre comme une application', done: d('ecran'), tour: 'pp-ecran' },
        { key: 'notifs', title: 'Activer les notifications', sub: 'Convocations, annulations, rappels la veille', done: d('notifs'), tour: 'notifications', cta: 'Activer' },
        role === 'parent'
          ? { key: 'repondre', title: `Répondre pour ${info.kidName ?? 'ton enfant'}`, sub: info.training ? `${info.training.when} · ${info.training.time} · prévenir seulement en cas d’absence` : 'Prévenir seulement en cas d’absence',
              done: d('repondre'), tour: 'pp-repondre', cta: 'Répondre' }
          : { key: 'repondre', title: info.training?.today ? 'Dire si tu viens ce soir' : 'Dire si tu viens',
              sub: info.training ? `${info.training.today ? info.training.title ?? 'Ce soir' : info.training.when} · ${info.training.time}` : 'Attendue par défaut : préviens seulement si tu ne peux pas',
              done: d('repondre'), tour: 'pp-repondre', cta: 'Répondre' },
      ];
    case 'partenaire':
      return [
        { key: 'fiche', title: 'Votre fiche partenaire', sub: 'Logo, offre aux licenciés, lien vers votre site', done: d('fiche') },
        { key: 'visibilite', title: 'Voir votre visibilité', sub: 'Page du club, matchs présentés, clics', done: d('visibilite'), tour: 'partenaire-visibilite' },
        { key: 'appel', title: 'Répondre à un appel du club', sub: info.ask ?? 'Les demandes du club arrivent sur votre accueil', done: d('appel'), tour: 'partenaire-appel' },
      ];
    default:
      return [];
  }
}

/** Titre et sous-titre du bloc. */
export function firstStepsHead(role: GuideRole, info: StepInfo): { title: string; sub?: string } {
  switch (role) {
    case 'bureau': return { title: 'Tes premiers pas' };
    case 'technique': return { title: 'Tes premiers pas de responsable technique', sub: 'quatre gestes pour lancer le projet sportif du club' };
    case 'coach': return { title: 'Tes premiers pas de coach', sub: `${info.teamName ? `${info.teamName} · ` : ''}quatre gestes pour que l’équipe vive dans l’app` };
    case 'joueur': case 'parent': return { title: '3 réglages, 1 minute' };
    case 'partenaire': return { title: 'Vos premiers pas', sub: [info.partnerName, info.clubName ? `partenaire ${deDu(info.clubName)}` : ''].filter(Boolean).join(' · ') };
    default: return { title: 'Tes premiers pas' };
  }
}

/** « du Basket Club de Rivebelle », « de l’AS Valmorin », « d’Étoile de Ferrandière ». */
export function deDu(club: string): string {
  const c = club.trim();
  if (/^(basket|club|cercle|stade|racing|foyer|bc|cb|sporting)\b/i.test(c)) return `du ${c}`;
  if (/^(as|us|es|asc|ascl|uscl|ab|union|association|entente|étoile|elan|élan|avenir|alliance)\b/i.test(c)) return /^[aeiouyhàâéèêëîïôûü]/i.test(c) ? `de l’${c}` : `de la ${c}`;
  return /^[aeiouyhàâéèêëîïôûü]/i.test(c) ? `d’${c}` : `de ${c}`;
}

/** Les « Bienvenue… » de l'accueil du bureau tant que les premiers pas ne sont pas finis. */
export const welcomeLine = (left: number) =>
  left >= 4 ? 'Bienvenue sur Arceau : cinq gestes pour lancer le club.'
  : left > 1 ? `Plus que ${left} étapes pour lancer le club.` : 'Plus qu’une étape pour lancer le club.';

/** Le compteur du bloc (« 1 / 3 ») et sa barre : les étapes à faire, sans celles acquises d'office ni les facultatives. */
export function stepProgress(steps: FirstStep[]): { done: number; total: number; counted: FirstStep[] } {
  const counted = steps.filter(s => !s.given && !s.optional);
  return { done: counted.filter(s => s.done).length, total: counted.length, counted };
}

/** Le prochain pas à faire (le premier qui ne l'est pas, hors des facultatives). */
export const nextStep = (steps: FirstStep[]) => steps.find(s => !s.done && !s.optional) ?? null;
